import { useCallback, useMemo, useState } from "react";

import { TransactionTypes } from "app/types";
import { AbfLoanExpanded } from "@bridgesplit/abf-react";

import {
    useWithdrawCollateral,
    usePrincipalRepayment,
    useAutofillClampNumber,
    useTouchGateSetter
} from "./payments/hooks";

export function useManualDecreaseLeverage({ loanExpanded }: { loanExpanded: AbfLoanExpanded | undefined }) {
    const [withdrawInputAmount, _setWithdrawInputAmount] = useState<number | undefined>(undefined);
    const [hasTouchedRepay, setHasTouchedRepay] = useState(false);

    const {
        inputAmount: repayInputAmount,
        handleInputChange: _setRepayAmount,
        setMaxRepay,
        balance: principalBalance,
        balanceLoading: principalBalanceLoading,
        insufficientBalance: principalInsufficientBalance,
        principalMetadata,
        submit: submitRepay,
        ctaError: repayError,
        isFullRepay
    } = usePrincipalRepayment({
        loanExpanded,
        collateralWithdrawAmount: withdrawInputAmount
    });

    const {
        setInputAmount: _setWithdrawInHook,
        maxWithdrawable,
        collateralMetadata,
        submit: submitWithdraw,
        ctaError: withdrawError
    } = useWithdrawCollateral({
        loanExpanded,
        additionalRepayAmount: repayInputAmount
    });

    const withdrawField = useAutofillClampNumber({
        max: maxWithdrawable,
        enabled: !!loanExpanded,
        gateAutofill: hasTouchedRepay,
        onValueChange: (v) => {
            _setWithdrawInputAmount(v);
            _setWithdrawInHook(v);
        }
    });

    const setRepayAmount = useTouchGateSetter(setHasTouchedRepay, _setRepayAmount, {
        onlyWhen: (v) => v !== undefined
    });

    const reset = useCallback(() => {
        _setRepayAmount(undefined);
        withdrawField.reset();
        setHasTouchedRepay(false);
    }, [_setRepayAmount, withdrawField]);

    const hasRepayInput = !!repayInputAmount && repayInputAmount > 0;
    const hasWithdrawInput = !!withdrawInputAmount && withdrawInputAmount > 0;

    const repayWarning = useMemo(() => {
        if (!principalInsufficientBalance) return undefined;
        return { message: "Insufficient balance", color: "warning" as const };
    }, [principalInsufficientBalance]);

    const ctaError = useMemo(() => {
        if (hasRepayInput) return repayError ?? null;
        if (hasWithdrawInput) return withdrawError ?? null;
        if (!repayInputAmount && !withdrawInputAmount) return "Enter amount";
        return null;
    }, [hasRepayInput, hasWithdrawInput, repayError, withdrawError, repayInputAmount, withdrawInputAmount]);

    const onSubmit = useCallback(async () => {
        if (hasRepayInput) {
            const result = await submitRepay();
            if (result?.isOk?.()) reset();
            return result;
        }
        if (hasWithdrawInput) {
            const result = await submitWithdraw();
            if (result?.isOk?.()) reset();
            return result;
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- fallback when neither repay nor withdraw path is taken
        return Promise.resolve(undefined as any);
    }, [hasRepayInput, hasWithdrawInput, submitRepay, submitWithdraw, reset]);

    const ctaText = useMemo(() => {
        if (ctaError) return ctaError;
        if (isFullRepay && hasRepayInput) return "Close Loop";
        if (hasRepayInput && hasWithdrawInput) return "Repay & Withdraw Collateral";
        if (hasRepayInput) return "Repay Principal";
        if (hasWithdrawInput) return "Withdraw collateral";
        return "Enter amount";
    }, [ctaError, isFullRepay, hasRepayInput, hasWithdrawInput]);

    const buttonProps = useMemo(
        () => ({
            disabled: !!ctaError,
            asyncCta: { onClickWithResult: onSubmit },
            transactionType:
                hasWithdrawInput && !hasRepayInput ? TransactionTypes.LoanWithdraw : TransactionTypes.Repay,
            children: ctaText
        }),
        [ctaError, onSubmit, ctaText, hasRepayInput, hasWithdrawInput]
    );

    return {
        repay: {
            value: repayInputAmount,
            setValue: setRepayAmount,
            metadata: principalMetadata,
            balance: principalBalance,
            balanceLoading: principalBalanceLoading,
            insufficientBalance: principalInsufficientBalance,
            setMax: setMaxRepay,
            warning: repayWarning,
            isFullRepay
        },
        withdraw: {
            value: withdrawInputAmount,
            setValue: withdrawField.setValue,
            metadata: collateralMetadata,
            balance: maxWithdrawable ?? 0,
            setMax: withdrawField.setMax,
            warning: undefined
        },
        buttonProps
    };
}
